import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { Pencil, Plus, Trash2 } from 'lucide-react'

import type { Phase } from '@/lib/api/phases-api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import {
    useCreatePhase,
    useDeletePhase,
    usePhases,
    useUpdatePhase,
} from '@/hooks/use-phases-query'
import { m } from '@/lib/paraglide/messages'
import { createPhaseSchema } from '@/lib/schemas/phase-schemas'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/agriculture/phases',
)({
    component: PhasesPage,
})

type FormValues = {
    name: string
    is_active: boolean
}

function PhasesPage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('phases.write')
    const [editing, setEditing] = useState<Phase | null>(null)
    const [open, setOpen] = useState(false)

    const { data } = usePhases({ per_page: 100 })
    const rows = data?.data ?? []

    const createMut = useCreatePhase()
    const updateMut = useUpdatePhase(editing?.id ?? '')
    const deleteMut = useDeletePhase()

    return (
        <div className="space-y-4 p-4">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-semibold">{m.phase_title()}</h1>
                {canWrite && (
                    <Button
                        size="sm"
                        onClick={() => {
                            setEditing(null)
                            setOpen(true)
                        }}
                    >
                        <Plus className="size-4" /> {m.phase_new()}
                    </Button>
                )}
            </div>

            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>{m.phase_name()}</TableHead>
                        <TableHead>{m.phase_active()}</TableHead>
                        <TableHead />
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row.id}>
                            <TableCell>{row.name}</TableCell>
                            <TableCell>
                                <Badge
                                    variant={
                                        row.is_active ? 'default' : 'secondary'
                                    }
                                >
                                    {row.is_active ? '✓' : '—'}
                                </Badge>
                            </TableCell>
                            <TableCell className="text-right space-x-1">
                                {canWrite && (
                                    <>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => {
                                                setEditing(row)
                                                setOpen(true)
                                            }}
                                        >
                                            <Pencil className="size-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => {
                                                if (
                                                    window.confirm(
                                                        m.phase_delete_confirm(),
                                                    )
                                                ) {
                                                    deleteMut.mutate(row.id)
                                                }
                                            }}
                                        >
                                            <Trash2 className="size-4" />
                                        </Button>
                                    </>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {editing ? m.phase_edit() : m.phase_new()}
                        </DialogTitle>
                    </DialogHeader>
                    <PhaseForm
                        initial={editing}
                        onSubmit={async (values) => {
                            if (editing) {
                                await updateMut.mutateAsync(values)
                            } else {
                                await createMut.mutateAsync(values)
                            }
                            setOpen(false)
                        }}
                        submitting={createMut.isPending || updateMut.isPending}
                    />
                </DialogContent>
            </Dialog>
        </div>
    )
}

function PhaseForm({
    initial,
    onSubmit,
    submitting,
}: {
    initial: Phase | null
    onSubmit: (v: FormValues) => Promise<void>
    submitting: boolean
}) {
    const schema = createPhaseSchema()
    const form = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: {
            name: initial?.name ?? '',
            is_active: initial?.is_active ?? true,
        },
    })
    return (
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
            <div>
                <Label>{m.phase_name()}</Label>
                <Input {...form.register('name')} />
                {form.formState.errors.name && (
                    <p className="text-xs text-red-500">
                        {form.formState.errors.name.message}
                    </p>
                )}
            </div>
            <div className="flex items-center gap-2">
                <Switch
                    checked={form.watch('is_active')}
                    onCheckedChange={(v) => form.setValue('is_active', v)}
                />
                <Label>{m.phase_active()}</Label>
            </div>
            <DialogFooter>
                <Button type="submit" disabled={submitting}>
                    {submitting ? '...' : m.phase_new()}
                </Button>
            </DialogFooter>
        </form>
    )
}
